import { isValidWinningTime, normalizeWinningTime } from './date-winning-schedules'
import type { GeneratedWinningTimeSlotInput } from './date-winning-schedules'

export const MIN_AUTO_GENERATED_WINNERS = 1
export const MAX_AUTO_GENERATED_WINNERS = 1000

export type GenerateRandomWinningTimesInput = {
  winnerCount: number
  startTime: string
  endTime: string
  occupiedTimes?: readonly string[]
}

export type GenerateRandomWinningTimesResult =
  | { ok: true; times: string[] }
  | { ok: false; error: string }

export function parseWinningTimeToMinutes(time: string): number | null {
  const normalized = normalizeWinningTime(time)
  if (!isValidWinningTime(normalized)) return null

  const [hours, minutes] = normalized.split(':').map(Number)
  return hours * 60 + minutes
}

export function formatMinutesToWinningTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function pickRandomDistinctMinutes(
  availableMinutes: number[],
  count: number,
  random: () => number
): number[] {
  const pool = [...availableMinutes]
  const picked: number[] = []

  for (let index = 0; index < count; index += 1) {
    const randomIndex = Math.floor(random() * pool.length)
    picked.push(pool[randomIndex]!)
    pool.splice(randomIndex, 1)
  }

  return picked.sort((left, right) => left - right)
}

export function generateRandomWinningTimes(
  input: GenerateRandomWinningTimesInput,
  random: () => number = Math.random
): GenerateRandomWinningTimesResult {
  const winnerCount = Math.trunc(input.winnerCount)

  if (!Number.isFinite(winnerCount) || winnerCount < MIN_AUTO_GENERATED_WINNERS) {
    return {
      ok: false,
      error: `Informe pelo menos ${MIN_AUTO_GENERATED_WINNERS} ganhador no dia.`,
    }
  }

  if (winnerCount > MAX_AUTO_GENERATED_WINNERS) {
    return {
      ok: false,
      error: `A quantidade máxima permitida é ${MAX_AUTO_GENERATED_WINNERS} ganhadores por dia.`,
    }
  }

  const startMinutes = parseWinningTimeToMinutes(input.startTime)
  const endMinutes = parseWinningTimeToMinutes(input.endTime)

  if (startMinutes === null || endMinutes === null) {
    return { ok: false, error: 'Informe horários de início e fim válidos.' }
  }

  if (startMinutes >= endMinutes) {
    return {
      ok: false,
      error: 'O horário de início deve ser anterior ao horário final.',
    }
  }

  const occupiedMinutes = new Set(
    (input.occupiedTimes ?? [])
      .map((time) => parseWinningTimeToMinutes(time))
      .filter((minutes): minutes is number => minutes !== null)
  )

  const availableMinutes: number[] = []
  for (let minute = startMinutes; minute <= endMinutes; minute += 1) {
    if (!occupiedMinutes.has(minute)) {
      availableMinutes.push(minute)
    }
  }

  if (availableMinutes.length < winnerCount) {
    return {
      ok: false,
      error:
        'O intervalo informado não comporta a quantidade de horários distintos solicitada. Amplie o período, reduza a quantidade de ganhadores ou revise os horários já cadastrados.',
    }
  }

  const pickedMinutes = pickRandomDistinctMinutes(availableMinutes, winnerCount, random)

  return {
    ok: true,
    times: pickedMinutes.map((minute) => formatMinutesToWinningTime(minute)),
  }
}

export type GenerateRandomWinningTimeSlotsInput = GenerateRandomWinningTimesInput & {
  contemplatedPrizeId: string
}

export type GenerateRandomWinningTimeSlotsResult =
  | { ok: true; slots: GeneratedWinningTimeSlotInput[] }
  | { ok: false; error: string }

export function generateRandomWinningTimeSlots(
  input: GenerateRandomWinningTimeSlotsInput,
  random: () => number = Math.random
): GenerateRandomWinningTimeSlotsResult {
  const contemplatedPrizeId = input.contemplatedPrizeId.trim()

  if (!contemplatedPrizeId) {
    return {
      ok: false,
      error: 'Selecione o prêmio que será contemplado nos horários gerados.',
    }
  }

  const timeResult = generateRandomWinningTimes(input, random)
  if (!timeResult.ok) {
    return timeResult
  }

  return {
    ok: true,
    slots: timeResult.times.map((time) => ({
      time,
      contemplated_prize_id: contemplatedPrizeId,
    })),
  }
}
